import { ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { type FC, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useDeleteTemplate, useDuplicateTemplate, useEditTemplate, useTemplateListQuery } from '@/api/queries/template';
import type { TagWithCount, TemplateTag } from '@/api/types/tag';
import type { TemplateListItem, TemplateListResponse, TemplateQuery } from '@/api/types/template';
import { AllTemplatesIcon } from '@/common/all-templates-icon';
import { BinIcon } from '@/common/bin-icon';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { CircularLoadingAnimation } from '@/common/circular-loading-animation';
import { ConfirmationModal, ConfirmationModalType } from '@/common/confimation-modal';
import { CopyIcon } from '@/common/copy-icon';
import { EditDocumentIcon } from '@/common/edit-document-icon';
import { EditIcon } from '@/common/edit-icon';
import { EmptyIcon } from '@/common/empty-icon';
import { FloatingPanel, FloatingPanelItem } from '@/common/floating-panel';
import { SortBy } from '@/common/sort-by';
import { TagIcon } from '@/common/tag-icon';
import { TemplateTile } from '@/common/template-tile';

import { CreateTemplateModal } from './components/create-modal';
import { DraftTemplateTile } from './components/draft-template-tile';
import { EditTagModal } from './components/edit-tag-modal';
import { EditTemplateTagModal } from './components/edit-template-tag-modal';

enum TemplateListSortBy {
    NEWEST = 'newest',
    OLDEST = 'oldest',
    A_Z = 'a-z',
    Z_A = 'z-a',
}

enum ListMode {
    ALL = 'ALL',
    DRAFT = 'DRAFT',
}

export const TemplateList: FC = () => {
    const [selectedTag, setSelectedTag] = useState<TagWithCount | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isEditTagModalOpen, setIsEditTagModalOpen] = useState<boolean>(false);
    const [isTemplateDeleteConfirmationOpen, setIsTemplateDeleteConfirmationOpen] = useState<boolean>(false);
    const [isEditTemplateTagModalOpen, setIsEditTemplateTagModalOpen] = useState<boolean>(false);
    const [templateList, setTemplateList] = useState<TemplateListItem[]>([]);
    const [draftTemplates, setDraftTemplates] = useState<TemplateListItem[]>([]);
    const [count, setCount] = useState<number>(0);
    const [draftCount, setDraftCount] = useState<number>(0);
    const [templateTags, setTemplateTags] = useState<TagWithCount[]>([]);
    const [selectedSortBy, setSelectedSortBy] = useState<string>(TemplateListSortBy.NEWEST);
    const [templateListMode, setTemplateListMode] = useState<ListMode>(ListMode.ALL);
    const [templateForDeletion, setTemplateForDeletion] = useState<TemplateListItem | null>(null);
    const [templateForTagEdit, setTemplateForTagEdit] = useState<TemplateListItem | null>(null);
    const [templateForEdit, setTemplateForEdit] = useState<TemplateListItem | null>(null);
    const draftContainerRef = useRef<HTMLDivElement>(null);
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [visibleDraftCount, setVisibleDraftCount] = useState(0);

    const [query, setQuery] = useState<TemplateQuery>({
        sort: TemplateListSortBy.NEWEST,
        draft: false,
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (isFetching) {
            return;
        }
        const updateVisibleDraftCount = () => {
            const cardGap = 16;
            const cardWidth = 300;
            if (draftContainerRef.current) {
                const containerWidth = draftContainerRef.current.offsetWidth;
                const count = Math.floor((containerWidth + cardGap) / (cardWidth + cardGap));
                setVisibleDraftCount(count === 0 ? 1 : count);
            }
        };

        const resizeObserver = new ResizeObserver(updateVisibleDraftCount);
        if (draftContainerRef.current) {
            resizeObserver.observe(draftContainerRef.current);
        }

        updateVisibleDraftCount();

        return () => resizeObserver.disconnect();
    }, [isFetching]);

    const draftContextMenuItems = (template: TemplateListItem) => [
        {
            name: 'Edit Draft',
            onClick: () => navigate(`/templates/${template.id}`),
            id: 'Edit Draft',
            icon: <EditDocumentIcon className="w-5 h-5" />,
        },
        {
            name: 'Delete Draft',
            onClick: () => setTemplateForDeletion(template),
            id: 'Delete Draft',
            color: 'red-50',
            icon: <BinIcon className="mb-1 w-5 h-5 stroke-red-50" />,
        },
    ];

    const contextMenuItems = (template: TemplateListItem) => [
        {
            name: 'Edit Template',
            onClick: () => navigate(`/templates/${template.id}`),
            id: 'Edit Template',
            icon: <EditDocumentIcon className="w-5 h-5" />,
        },
        {
            name: 'Rename Template',
            onClick: () => setTemplateForEdit(template),
            id: 'Rename Template',
            icon: <EditIcon className="w-5 h-5 stroke-white" />,
        },
        {
            name: 'Edit Tags',
            onClick: () => setTemplateForTagEdit(template),
            id: 'Edit Tags',
            icon: <TagIcon className="w-5 h-5" />,
        },
        {
            name: 'Duplicate Template',
            onClick: () =>
                duplicateTemplate(template.id, {
                    onSuccess: (obj) => navigate(`/templates/${(obj as TemplateListItem).id}`),
                }),
            id: 'Duplicate Template',
            icon: <CopyIcon className="w-5 h-4 stroke-white" />,
        },
        {
            name: 'Delete Template',
            onClick: () => setTemplateForDeletion(template),
            id: 'Delete Template',
            color: 'red-50',
            icon: <BinIcon className="mb-1 w-5 h-5 stroke-red-50" />,
        },
    ];

    const { refetch: refetchTemplateList } = useTemplateListQuery(query, {
        onSuccess: (data: TemplateListResponse) => {
            setTemplateList(data.results);
            setCount(data.totalCount);
            setDraftCount(data.draftCount);
            setDraftTemplates(data.recentDrafts);
            setTemplateTags(data.tags);
            setIsFetching(false);
        },
    });

    const { mutate: deleteTemplate } = useDeleteTemplate();
    const { mutate: duplicateTemplate } = useDuplicateTemplate();
    const { mutate: editTemplate } = useEditTemplate();

    const updateSelectedTag = (tag: TagWithCount | null) => {
        setSelectedTag(tag);
        setTemplateListMode(ListMode.ALL);
    };

    const handleTemplateTagUpdate = (tags: TemplateTag[]) => {
        if (!templateForTagEdit) return;
        setTemplateForTagEdit({ ...templateForTagEdit, tags });
        setTemplateList((prev) =>
            prev.map((t) => {
                if (t.id === templateForTagEdit.id) {
                    return { ...t, tags };
                }
                return t;
            }),
        );
    };

    const updateTemplateNameInState = (templateId: number, name: string, isDraft: boolean) => {
        if (isDraft) {
            setDraftTemplates((prev) =>
                prev.map((t) => {
                    if (t.id === templateId) {
                        return { ...t, name: name };
                    }
                    return t;
                }),
            );
            return;
        }
        setTemplateList((prev) =>
            prev.map((t) => {
                if (t.id === templateId) {
                    return { ...t, name: name };
                }
                return t;
            }),
        );
    };

    const updateTemplateName = (template: TemplateListItem, name: string, isDraft: boolean) => {
        const oldValue = template.name;
        updateTemplateNameInState(template.id, name, isDraft);
        editTemplate(
            { id: template.id, data: { name: name } },
            {
                onError: () => {
                    updateTemplateNameInState(template.id, oldValue, isDraft);
                },
            },
        );
        setTemplateForEdit(null);
    };

    const setDraftTab = () => {
        setTemplateListMode(ListMode.DRAFT);
        setSelectedTag(null);
    };

    const getTagTile = (templateTag: TagWithCount) => {
        const count = templateTag.count;
        return (
            <div
                onClick={
                    templateTag.count + templateTag.draftCount > 0 ? () => updateSelectedTag(templateTag) : () => {}
                }
                onKeyUp={
                    templateTag.count + templateTag.draftCount > 0
                        ? (e) => {
                              if (e.key === 'Enter') updateSelectedTag(templateTag);
                          }
                        : () => {}
                }
                className={`flex flex-row justify-between items-center rounded-md px-2.5 py-1.5 hover:cursor-pointer hover:bg-gray-150 transition-colors ${selectedTag?.id !== templateTag.id ? '' : 'bg-gray-100'}`}
                key={templateTag.id}
            >
                <span
                    className={`text-xs font-normal ${selectedTag?.id !== templateTag.id ? 'text-gray-60' : 'text-white'}`}
                >
                    {templateTag.name}
                </span>
                <p
                    className={`flex h-6 w-6 bg-gray-100 rounded-md ${selectedTag?.id !== templateTag.id ? 'text-gray-60' : 'text-white'} text-xs text-center justify-center items-center`}
                >
                    {count}
                </p>
            </div>
        );
    };

    const getTitle = () => {
        let title = '';
        if (templateListMode === ListMode.ALL && (templateList.length > 0 || draftTemplates.length > 0)) {
            title = selectedTag ? selectedTag.name : 'All Templates';
        } else if (templateListMode === ListMode.DRAFT) {
            title = 'Drafts';
        }
        return title;
    };

    useEffect(() => {
        const query: { sort: string; draft: boolean; tag?: number } = {
            sort: selectedSortBy,
            draft: templateListMode === ListMode.DRAFT,
        };
        if (selectedTag) {
            query.tag = selectedTag.id;
        }
        setQuery(query);
    }, [selectedTag, selectedSortBy, templateListMode]);

    useEffect(() => {
        if (templateForDeletion !== null) {
            setIsTemplateDeleteConfirmationOpen(true);
        }
    }, [templateForDeletion]);

    useEffect(() => {
        if (!isTemplateDeleteConfirmationOpen) {
            setTemplateForDeletion(null);
        }
    }, [isTemplateDeleteConfirmationOpen]);

    useEffect(() => {
        if (templateForTagEdit !== null) {
            setIsEditTemplateTagModalOpen(true);
        }
    }, [templateForTagEdit]);

    useEffect(() => {
        if (!isEditTemplateTagModalOpen) {
            setTemplateForTagEdit(null);
        }
    }, [isEditTemplateTagModalOpen]);

    return (
        <>
            <div className="flex min-h-0 flex-row h-full">
                {isFetching ? (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                        <CircularLoadingAnimation className="w-16 h-16" />
                        <p className="text-gray-60 text-2xl">Loading templates ...</p>
                    </div>
                ) : (
                    <>
                        <FloatingPanel
                            className="px-4 h-full w-[250px] flex-shrink-0 overflow-y-auto"
                            omitSeperator={true}
                        >
                            <FloatingPanelItem className="px-4 my-2">
                                <Button
                                    className="w-full"
                                    title="Create Template"
                                    buttonType={ButtonType.PRIMARY}
                                    buttonSize={ButtonSize.LARGE}
                                    leading={<PlusIcon className="h-5 w-5 text-white" />}
                                    onClick={() => setIsCreateModalOpen(true)}
                                />
                                <hr className="bg-gray-100 border-gray-100 w-full mt-4" />
                            </FloatingPanelItem>
                            <FloatingPanelItem className="flex flex-col mt-2 gap-2 px-4">
                                <div
                                    onClick={() => {
                                        updateSelectedTag(null);
                                        setTemplateListMode(ListMode.ALL);
                                    }}
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter') {
                                            updateSelectedTag(null);
                                            setTemplateListMode(ListMode.ALL);
                                        }
                                    }}
                                    className={`flex flex-row justify-between items-center rounded-md px-2.5 py-1.5 hover:cursor-pointer hover:bg-gray-150 transition-colors ${templateListMode === ListMode.ALL && !selectedTag ? 'bg-gray-100' : ''}`}
                                >
                                    <div className="flex flex-row gap-2.5 items-center">
                                        <AllTemplatesIcon
                                            className={`w-4 h-4 ${templateListMode === ListMode.ALL && !selectedTag ? 'stroke-white' : 'stroke-gray-60'}`}
                                        />{' '}
                                        <span
                                            className={`${templateListMode === ListMode.ALL && !selectedTag ? 'text-white' : 'text-gray-60'} text-sm font-normal`}
                                        >
                                            All Templates
                                        </span>
                                    </div>
                                    <p
                                        className={`flex h-6 w-6 bg-gray-100 rounded-md ${templateListMode === ListMode.ALL && !selectedTag ? 'text-white' : 'text-gray-60'} text-xs text-center justify-center items-center`}
                                    >
                                        {count}
                                    </p>
                                </div>
                                <div className="ml-6">
                                    {draftCount > 0 && (
                                        <div
                                            onClick={setDraftTab}
                                            onKeyUp={(e) => {
                                                if (e.key === 'Enter') setDraftTab();
                                            }}
                                            className={`flex flex-row justify-between items-center rounded-md px-2.5 py-1.5 hover:cursor-pointer hover:bg-gray-150 transition-colors ${templateListMode === ListMode.DRAFT ? 'bg-gray-100' : ''}`}
                                        >
                                            <span
                                                className={`${templateListMode === ListMode.DRAFT ? 'text-white' : 'text-gray-60'} text-xs font-normal`}
                                            >
                                                Drafts
                                            </span>
                                            <p
                                                className={`flex h-6 w-6 bg-gray-100 rounded-md ${templateListMode === ListMode.DRAFT ? 'text-white' : 'text-gray-60'} text-xs text-center justify-center items-center`}
                                            >
                                                {draftCount}
                                            </p>
                                        </div>
                                    )}
                                    {draftCount > 0 && (
                                        <div className="border-b-[1px] border-solid border-gray-100 ml-3 mr-3 mt-1 mb-2" />
                                    )}
                                    {templateTags
                                        .filter((tag) => tag.count > 0)
                                        .map((templateTag) => getTagTile(templateTag))}
                                </div>
                                <button
                                    type="button"
                                    className="group flex flex-row items-center gap-2 w-full py-2 text-blue-30 hover:text-blue-50 text-xs"
                                    onClick={() => setIsEditTagModalOpen(true)}
                                >
                                    <div className="w-4 h-4" />
                                    <EditIcon className="stroke-blue-30 group-hover:stroke-blue-50" />
                                    Edit tags
                                </button>
                            </FloatingPanelItem>
                        </FloatingPanel>
                        <div className="mt-2 p-5 flex flex-col flex-grow overflow-hidden">
                            <div className="flex flex-row justify-between">
                                <p className="text-white text-2xl font-normal items-start">{getTitle()}</p>
                                <SortBy
                                    options={[
                                        { name: 'Newest', id: TemplateListSortBy.NEWEST },
                                        { name: 'Oldest', id: TemplateListSortBy.OLDEST },
                                        { name: 'A-Z', id: TemplateListSortBy.A_Z },
                                        { name: 'Z-A', id: TemplateListSortBy.Z_A },
                                    ]}
                                    onChange={(setSortBy) => setSelectedSortBy(setSortBy)}
                                />
                            </div>
                            {templateList.length === 0 && draftCount === 0 && (
                                <div className="flex flex-col items-center justify-center flex-grow">
                                    <EmptyIcon className="h-32 w-28" />
                                    <p className="text-white text-3xl font-normal mt-10">Create your First Template</p>
                                    <Button
                                        className="mt-4"
                                        title="Create Template"
                                        buttonType={ButtonType.PRIMARY}
                                        buttonSize={ButtonSize.LARGE}
                                        leading={<PlusIcon className="h-5 w-5 text-white" />}
                                        onClick={() => setIsCreateModalOpen(true)}
                                    />
                                </div>
                            )}
                            <div className="min-h-0 overflow-y-scroll flex-1 no-scrollbar">
                                <div ref={draftContainerRef} className="w-full">
                                    {draftCount > 0 && templateListMode === ListMode.ALL && !selectedTag && (
                                        <div className="flex flex-col w-full bg-gray-150 mt-8 p-6 rounded-lg">
                                            <p className="text-gray-40 text-lg">Drafts</p>
                                            <div className="flex gap-4 overflow-hidden mt-3">
                                                {draftTemplates.slice(0, visibleDraftCount).map((template, index) => (
                                                    <DraftTemplateTile
                                                        key={template.id}
                                                        title={template.name}
                                                        createdAt={template.createdAt}
                                                        isRenaming={templateForEdit === template}
                                                        onRename={(value) => updateTemplateName(template, value, true)}
                                                        menuItems={draftContextMenuItems(template)}
                                                        thumbnail={template.latestVersion?.thumbnail || ''}
                                                        onClick={() => navigate(`/templates/${template.id}`)}
                                                    />
                                                ))}
                                            </div>
                                            {draftCount > visibleDraftCount && (
                                                <button
                                                    type="button"
                                                    className="flex flex-row gap-2 items-center mt-3 w-32"
                                                    onClick={() => {
                                                        setTemplateListMode(ListMode.DRAFT);
                                                    }}
                                                >
                                                    <span className="text-blue-30 text-xs">Show more</span>
                                                    <ChevronRightIcon className="w-4 h-4 text-blue-30" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {templateList.length > 0 && (
                                    <div className="mt-10 no-scrollbar">
                                        <div className="grid gap-6 justify-stretch grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] max-sm:grid-cols-1">
                                            {templateList.map((template) => (
                                                <TemplateTile
                                                    key={template.id}
                                                    title={template.name}
                                                    createdAt={template.createdAt}
                                                    tags={template.tags}
                                                    thumbnail={template.latestVersion?.thumbnail || ''}
                                                    isRenaming={templateForEdit === template}
                                                    isDraft={template.publishedVersion === null}
                                                    onRename={(value) => updateTemplateName(template, value, false)}
                                                    menuItems={contextMenuItems(template)}
                                                    onClick={() => navigate(`/templates/${template.id}`)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {templateList.length === 0 &&
                                    draftTemplates.length > 0 &&
                                    templateListMode === ListMode.ALL && (
                                        <div className="flex flex-col items-center justify-center w-full mt-8">
                                            <hr className="bg-gray-100 border-gray-100 w-full" />
                                            <p className="text-xl text-gray-40 mt-8">No Published Templates</p>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <CreateTemplateModal
                isOpen={isCreateModalOpen}
                setIsOpen={setIsCreateModalOpen}
                onSuccess={(id) => navigate(`/templates/${id}`)}
            />
            <EditTagModal
                isOpen={isEditTagModalOpen}
                setIsOpen={setIsEditTagModalOpen}
                onDone={() => {
                    refetchTemplateList();
                }}
            />
            {templateForTagEdit && (
                <EditTemplateTagModal
                    isOpen={isEditTemplateTagModalOpen}
                    setIsOpen={setIsEditTemplateTagModalOpen}
                    template={templateForTagEdit}
                    setTags={handleTemplateTagUpdate}
                    onDone={refetchTemplateList}
                />
            )}
            <ConfirmationModal
                isOpen={isTemplateDeleteConfirmationOpen}
                setIsOpen={setIsTemplateDeleteConfirmationOpen}
                type={ConfirmationModalType.WARN}
                confirmText="Delete"
                cancelText="Cancel"
                message="Delete Template"
                subMessage="Are you sure you want to delete this template?"
                onConfirm={() => {
                    deleteTemplate(templateForDeletion?.id ?? -1);
                }}
            />
        </>
    );
};
